import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import { useCallback } from "react"
import {
  usePaginatedTableWithUrlState,
  SearchInput,
  Button,
  Skeleton,
  EmptyState,
  MAX_PAGINATED_PAGE_SIZE,
  createTableSearchSchema,
  cn,
  LearningPathCategory,
  learningPathCategories,
  learningPathCategory,
} from "toptaal-shared"
import { getFilteredLearningPaths } from "@/api/services/learningPaths"
import LearningPathRow from "@/pages/leerlijnen/LearningPathRow"
import { PlusIcon } from "@heroicons/react/24/outline"
import { LearningPath } from "toptaal-shared"
import { learningPathsQueryKeys } from "@/api/query-keys/learningPaths"
import RoleBasedComponent from "@/components/RolebasedComponent"
import { useLearningPathCreateStore } from "@/stores/useLearningPathCreateStore"

export const Route = createFileRoute("/beheer/leerlijnen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "curriculum"),
  validateSearch: createTableSearchSchema().extend({
    learningPathCategory: learningPathCategory.optional(),
  }),
})

function RouteComponent() {
  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/leerlijnen/" })
  const navigate = useNavigate({ from: "/beheer/leerlijnen/" })
  const category =
    searchParams.learningPathCategory ?? learningPathCategories[0].key
  const { clearCreateLearningPathStore } = useLearningPathCreateStore()
  const { isFetching, data, search, setSearch } = usePaginatedTableWithUrlState(
    {
      queryKey: learningPathsQueryKeys.category(category),
      queryFn: (filters) =>
        getFilteredLearningPaths({
          ...filters,
          learningPathCategory: category,
        }),
      pageSize: MAX_PAGINATED_PAGE_SIZE,
      searchParams,
      navigate,
      storageKey: "/beheer/leerlijnen",
    },
  )

  const handleNavigateDetail = useCallback(
    (learningPathId: LearningPath["id"]) => {
      router.navigate({
        to: "/beheer/leerlijnen/$learningPathId",
        params: { learningPathId: learningPathId.toString() },
        search: { from: router.state.location.href },
      })
    },
    [router],
  )

  const handleCategoryChange = useCallback(
    (newCategory: LearningPathCategory) => {
      navigate({
        search: (prev) => ({
          ...prev,
          learningPathCategory: newCategory,
          page: 1,
        }),
        replace: true,
      })
    },
    [navigate],
  )

  const handleCreateNew = useCallback(() => {
    clearCreateLearningPathStore()
    router.navigate({
      to: "/beheer/leerlijnen/aanmaken",
    })
  }, [router, clearCreateLearningPathStore])

  const onSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)
    },
    [setSearch],
  )

  return (
    <ScreenWrapper showManagementMenu>
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-xl">Leerlijnen</h3>
        <div className="flex items-center justify-end gap-4 py-4">
          <SearchInput
            placeholder="Filter leerlijnen..."
            value={search}
            onChange={onSearch}
            className="max-w-sm border-primary-accent-blue border"
          />

          <RoleBasedComponent entity="curriculum" permissions="create">
            <Button onClick={handleCreateNew}>
              <PlusIcon className="size-5" />
              Nieuwe leerlijn
            </Button>
          </RoleBasedComponent>
        </div>
      </div>

      <div className="flex">
        {learningPathCategories.map((cat) => (
          <Button
            key={cat.key}
            variant="ghost"
            onClick={() => handleCategoryChange(cat.key)}
            className={cn(
              "rounded-b-none border border-b-none border-light-grey font-normal",
              {
                "bg-primary-accent-blue font-bold": category === cat.key,
              },
            )}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      <div className="rounded-b border-b-none border-t-none border border-light-grey bg-white">
        {isFetching ? (
          <div className="p-8">
            <Skeleton className="h-32" />
          </div>
        ) : data?.data.learningPaths && data.data.learningPaths.length > 0 ? (
          data.data.learningPaths.map((learningPath) => (
            <LearningPathRow
              key={learningPath.id}
              learningPath={learningPath}
              onRowClick={handleNavigateDetail}
            />
          ))
        ) : (
          <EmptyState className="p-8 text-center">
            Geen leerlijnen gevonden
          </EmptyState>
        )}
      </div>
    </ScreenWrapper>
  )
}
